import React, { useState, useEffect, useContext } from "react"
import { Content } from "./style"
import freight_act from '../../../assets/router/routersTabHeader/freight-active.svg'
import local_act from '../../../assets/sidemenu/local-active.svg'
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router-dom"
import { TypeMainOutlet } from ".."
import TableFiltersContext from "../../../contexts/TableFiltersContext"
import { FreightContext } from "../../../contexts/FreightContext"
import { VehicleContext } from "../../../contexts/VehicleContext"
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { PageHeader } from '../../../components/StandardPage'
import { ViewModeSelector } from '../Pipelines/PipelineControls'
import { Layers, Table } from 'lucide-react'

export type FreightViewMode = 'grouped' | 'standard'

export interface RouterOutletType {
    tabIndex: number
    setTabIndex: React.Dispatch<React.SetStateAction<number>>
    search: string
    appliedFilters: Filter[]
    refresh: boolean
    setLoadingHeader: React.Dispatch<React.SetStateAction<boolean>>
    viewMode?: FreightViewMode
}

const tabs = [
    { path: '', image: freight_act, name: 'Fretes' },
    { path: 'routers', image: local_act, name: 'Roteiros' }
]

export const freightHeaders: Header[] = [
    { key: 'companyName', label: 'Cliente', type: 'string' },
    { key: 'orderNumber', label: 'Numero de venda', type: 'string' },
    { key: 'type', label: 'Tipo', type: 'string', selectOptions: [
        { value: 'Entrega', label: 'Entrega' },
        { value: 'Retirada', label: 'Retirada' },
        { value: 'Sem endereço', label: 'Sem endereço' },
    ]},
    { key: 'status', label: 'Status', type: 'string', selectOptions: [
        { value: 'A programar', label: 'A programar' },
        { value: 'Rota programada', label: 'Rota programada' },
        { value: 'Entregue', label: 'Entregue' },
        { value: 'Coletado', label: 'Coletado' },
        { value: 'Endereço pendente', label: 'Endereço pendente' },
        { value: 'Programado', label: 'Programado' },
        { value: 'Em trânsito', label: 'Em trânsito' },
        { value: 'Concluído', label: 'Concluído' },
        { value: 'Liberado', label: 'Liberado' },
    ]},
    { key: 'deliveryAddress', label: 'Endereço de entrega', type: 'string' },
    { key: 'load', label: 'Carga', type: 'string' },
    { key: 'readyToShip', label: 'Liberado pra expedir', type: 'string', selectOptions: [
        { value: 'Liberado', label: 'Liberado' },
        { value: 'Não liberado', label: 'Não liberado' },
    ]},
    { key: 'readyToShipDate', label: 'Data liberada para expedir', type: 'date' },
    { key: 'deadLine', label: 'Data limite de entrega', type: 'date' },
    { key: 'observation', label: 'Observações', type: 'string' },
]

export const routeHeaders: Header[] = [
    { key: 'id', label: 'Número', type: 'number' },
    { key: 'origin', label: 'Origem', type: 'string' },
    { key: 'status', label: 'Status', type: 'string', selectOptions: [
        { value: 'A programar', label: 'A programar' },
        { value: 'Rota programada', label: 'Rota programada' },
        { value: 'Rota concluida', label: 'Rota concluida' },
        { value: 'Programado', label: 'Programado' },
        { value: 'Em trânsito', label: 'Em trânsito' },
        { value: 'Concluído', label: 'Concluído' },
    ]},
    { key: 'vehiclePlate', label: 'Placa', type: 'string' },
    { key: 'vehicleInfo', label: 'Veículo', type: 'string' },
    { key: 'responsible', label: 'Responsável da Rota', type: 'string' },
    { key: 'companyDestination', label: 'Voltar à origem', type: 'string', selectOptions: [
        { value: 'Sim', label: 'Sim' },
        { value: 'Não', label: 'Não' },
    ]},
    { key: 'freights', label: 'Fretes selecionados', type: 'string' },
    { key: 'date', label: 'Data', type: 'date' },
    { key: 'track', label: 'Tempo de trajeto', type: 'string' },
]

const RouterContent = () => {
    const [tabIndex, setTabIndex] = useState<number>(0)
    const [refresh, setRefresh] = useState<boolean>(false)
    const [loadingHeader, setLoadingHeader] = useState<boolean>(false)
    const { search } = useOutletContext<TypeMainOutlet>()
    const location = useLocation()
    const navigate = useNavigate()
    const { setFreightStatusFilter, setRouterStatusFilter } = useContext(TableFiltersContext)
    const { setFreight } = useContext(FreightContext)
    const { setVehicle } = useContext(VehicleContext)

    const freightFiltersState = usePersistedFilters('freight')
    const routeFiltersState = usePersistedFilters('route')

    const [freightViewMode, setFreightViewMode] = useState<FreightViewMode>(() => {
        const saved = localStorage.getItem('router_freight_view_mode') as FreightViewMode
        return saved === 'grouped' ? 'grouped' : 'standard'
    })

    const handleViewModeChange = (mode: FreightViewMode) => {
        setFreightViewMode(mode)
        localStorage.setItem('router_freight_view_mode', mode)
    }

    const viewModeOptions = [
        { value: 'standard', label: 'Padrão', icon: <Table size={16} /> },
        { value: 'grouped', label: 'Agrupada', icon: <Layers size={16} /> },
    ]

    const activeFiltersState = tabIndex === 0 ? freightFiltersState : routeFiltersState
    const activeHeaders = tabIndex === 0 ? freightHeaders : routeHeaders

    useEffect(() => {
        if (location.pathname.includes('/router/routers')) {
            setTabIndex(1)
        } else {
            setTabIndex(0)
        }
    }, [location.pathname])

    useEffect(() => {
        const statusFilters = freightFiltersState.appliedFilters.filter(f => f.key === 'status')
        if (statusFilters.length > 1) {
            setFreightStatusFilter('Todos')
        } else if (statusFilters.length === 1) {
            setFreightStatusFilter(String(statusFilters[0].value))
        } else {
            setFreightStatusFilter('Todos')
        }
    }, [freightFiltersState.appliedFilters, setFreightStatusFilter])

    useEffect(() => {
        const statusFilters = routeFiltersState.appliedFilters.filter(f => f.key === 'status')
        if (statusFilters.length > 1) {
            setRouterStatusFilter('Todos')
        } else if (statusFilters.length === 1) {
            setRouterStatusFilter(String(statusFilters[0].value))
        } else {
            setRouterStatusFilter('Todos')
        }
    }, [routeFiltersState.appliedFilters, setRouterStatusFilter])

    const isCreateOrEdit = location.pathname === '/panel/router/routers/create' || location.pathname === '/panel/router/routers/edit'

    const criarFrete = () => {
        setFreight(null)
        setVehicle(null)
        navigate('/panel/router/create')
    }

    const criarRota = () => {
        navigate('/panel/router/routers/create')
    }

    const createButtonConfig = tabIndex === 0
        ? { label: 'Criar frete', onClick: criarFrete }
        : { label: 'Criar rota', onClick: criarRota }

    return (
        <>
            <PageHeader
                title='Roterização'
                tableName={tabIndex === 0 ? 'Freight' : 'Route'}
                moduleName={tabIndex === 0 ? 'Fretes' : 'Rotas'}
                tabs={!isCreateOrEdit ? tabs.map((tab, index) => ({ key: String(index), label: tab.name, icon: tab.image })) : undefined}
                activeTab={String(tabIndex)}
                onTabChange={(key) => {
                    const index = Number(key)
                    if (index !== tabIndex) navigate(tabs[index].path)
                }}
                onReload={() => setRefresh((p) => !p)}
                isLoading={loadingHeader}
                filterHeaders={!isCreateOrEdit ? activeHeaders : undefined}
                appliedFilters={!isCreateOrEdit ? activeFiltersState.appliedFilters : undefined}
                onApplyFilter={activeFiltersState.handleApplyFilter}
                onRemoveFilter={activeFiltersState.handleRemoveFilter}
                onClearAllFilters={activeFiltersState.handleClearAll}
                createButton={!isCreateOrEdit ? createButtonConfig : undefined}
                rightExtra={
                    !isCreateOrEdit && tabIndex === 0 ? (
                        <ViewModeSelector
                            value={freightViewMode}
                            onChange={(mode) => handleViewModeChange(mode as FreightViewMode)}
                            options={viewModeOptions}
                        />
                    ) : undefined
                }
            />

            <Content>
                <Outlet
                    context={{
                        tabIndex,
                        setTabIndex,
                        search,
                        appliedFilters: activeFiltersState.appliedFilters,
                        refresh,
                        setLoadingHeader,
                        viewMode: freightViewMode,
                    }}
                />
            </Content>
        </>
    )
}

export default RouterContent
